import { createContext, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../store/auth";
import { useMeta } from "../hooks";
import { useNotifications, requestBrowserPermission } from "../store/notifications";
import { Avatar } from "./ui";
import Icon from "./Icon";
import Footer from "./Footer";
import HelpButton from "./Help";
import InstallPrompt from "./InstallPrompt";
import MessagingDock from "./MessagingDock";
import AnunciosPopup from "./AnunciosPopup";
import Spotlight from "./Spotlight";
import { ROLE_LABEL } from "../constants";
import { fmtStamp, applyAppTimezone } from "../utils";
import { useT } from "../i18n";
import { getNavLayout, getSidebarCollapsed, setSidebarCollapsed } from "../store/appearance";

// Acento por módulo para el ítem ACTIVO del nav. Es uno de los tres únicos
// sitios donde aparece el color del módulo (los otros dos: el tag junto al
// título de la página y el botón primario de la pantalla).
// La clave es el prefijo de ruta del módulo; se resuelve con `moduloDe()`.
// Cada módulo aporta [clases de acento, nombre de icono]. Los iconos salen del
// set de Icon.jsx (SVG inline, stroke = currentColor) y se eligieron por
// legibilidad a 15px: `check` para tareas (convención de to-do), `ticket` para
// Soporte (su objeto de dominio), `laptop` para Activos (equipo de TI, no
// `package` que se leería como envíos), `receipt` para Compras (factura/OC, no
// `truck` que sería despacho), `globe` para Tiendas (lo que distingue "en
// línea") y `briefcase` para Herramientas (`settings` está tomado por Ajustes).
const NAV_MODULO = {
  "/mis-recursos": ["bg-activos-bg text-activos", "laptop", "#0D9488"],
  "/soporte": ["bg-soporte-bg text-soporte", "ticket", "#DB2777"],
  "/compras": ["bg-compras-bg text-compras", "receipt", "#D97706"],
  "/itam": ["bg-activos-bg text-activos", "laptop", "#0D9488"],
  "/proyectos-activos": ["bg-reportes-bg text-reportes", "chart", "#4F46E5"],
  "/proyectos": ["bg-proyectos-bg text-proyectos", "folder", "#7C3AED"],
  "/timeline": ["bg-proyectos-bg text-proyectos", "folder", "#7C3AED"],
  "/tiendas": ["bg-tiendas-bg text-tiendas", "globe", "#0284C7"],
  "/dashboard": ["bg-reportes-bg text-reportes", "chart", "#4F46E5"],
  "/metricas": ["bg-reportes-bg text-reportes", "chart", "#4F46E5"],
  "/documentacion": ["bg-herramientas-bg text-herramientas", "briefcase", "#0891B2"],
  "/departamento": ["bg-tareas-bg text-tareas", "check", "#2F6FE0"],
};
const MOD_TAREAS = ["bg-tareas-bg text-tareas", "check", "#2F6FE0"];   // "/" = Mis Tareas

/** [acento, icono] del ítem de nav según su ruta.
 *  Ojo con el orden de las claves: "/proyectos-activos" (Reportes) va ANTES
 *  que "/proyectos" porque el match es por prefijo. */
function modulo(to = "") {
  if (to === "/") return MOD_TAREAS;
  const k = Object.keys(NAV_MODULO).find((pref) => to.startsWith(pref));
  return k ? NAV_MODULO[k] : MOD_TAREAS;
}
const moduloDe = (to) => modulo(to)[0];
const iconoDe = (to) => modulo(to)[1];
/** Hex del acento del módulo, para la variable CSS `--acento`. */
const acentoDe = (to) => modulo(to)[2];

const NAV = [
  // Tareas: agrupa la vista personal y el tablero del departamento.
  // Al hacer clic en la pestaña entra a "Mis Tareas" (la opción por defecto).
  // `alsoReadonly`: el menú Tareas también se muestra a roles de solo-lectura
  // (p. ej. Directivo) para que lleguen al Tablero del departamento y vean las
  // tareas de su(s) área(s). Antes tenía `hideReadonly` y el menú desaparecía
  // entero, así que un directivo no tenía dónde ver las tareas. Pedido 2026-09-29.
  { to: "/", label: "Tareas", k: "nav.tasks", end: true, alsoReadonly: true, children: [
    { to: "/", label: "Mis Tareas", k: "nav.mytasks", end: true, hideReadonly: true },
    { to: "/departamento", label: "Tablero", k: "nav.board", cap: "department_board" },
  ]},
  { to: "/proyectos", label: "Proyectos", k: "nav.projects", cap: "department_board", alsoReadonly: true, children: [
    { to: "/proyectos", label: "Todos los proyectos", k: "nav.projects" },
    { to: "/timeline", label: "Timeline", k: "nav.timeline", hideReadonly: true },
  ]},
  // Logística entrante (Federación Fase 2): espejo read-only de contenedores de
  // otra instancia. `feature` lo muestra SOLO en la instancia consumidora (GDVzla).
  { to: "/logistica-entrante", label: "Logística entrante", k: "nav.logistica_entrante", cap: "department_board", feature: "federacion_entrante", alsoReadonly: true },
  // Cada hijo puede llevar su propia cap: el menú solo muestra los reportes
  // que el rol tiene permitidos en la matriz (Ajustes → Permisos).
  // Compras (P2P portado): el ciclo del gasto en su orden real. Se irá
  // completando fase por fase (requisición → OC → factura → pago).
  // Soporte (mesa de ayuda). Igual que Compras: `feature` lo oculta mientras el
  // módulo esté apagado en Ajustes, y `cap` respeta la matriz de permisos.
  { to: "/soporte", label: "Soporte", k: "nav.soporte", cap: "soporte", feature: "soporte", children: [
    { heading: "Trabajo diario" },
    { to: "/soporte", label: "Mi mesa de soporte" },
    { to: "/soporte/tickets", label: "Bandeja de tickets" },
    { to: "/soporte/tablero", label: "Tablero (Kanban)" },
    { to: "/soporte/agenda", label: "Agenda de acciones" },
    { to: "/soporte/whatsapp", label: "WhatsApp (triage)", cap: "soporte_gestion" },
    { heading: "Clientes y conocimiento" },
    { to: "/soporte/clientes", label: "Clientes y contactos", cap: "soporte_gestion" },
    { to: "/soporte/kb", label: "Base de conocimiento" },
    { to: "/soporte/manuales", label: "Manuales" },
    { heading: "Reportes" },
    { to: "/soporte/panel", label: "Panel de estadísticas" },
    { to: "/soporte/analitica", label: "Analítica v2", cap: "soporte_gestion" },
    { to: "/soporte/efectividad", label: "Efectividad de agentes", cap: "soporte_admin" },
    { to: "/soporte/resumen-semanal", label: "Resumen semanal", cap: "soporte_admin" },
    { heading: "Configuración" },
    { to: "/soporte/configuracion", label: "Configuración", cap: "soporte_admin" },
  ] },
  { to: "/compras/requisiciones", label: "Compras", k: "nav.compras", anyCap: ["compras", "compras_gestion", "compras_aprobar", "compras_directa"], feature: "compras", children: [
    { heading: "El ciclo del gasto" },
    { to: "/compras/requisiciones", label: "1 · Requisiciones", anyCap: ["compras", "compras_directa"] },
    { to: "/compras/aprobaciones", label: "2 · Aprobaciones pendientes", badge: "compras_aprobaciones", cap: "compras_aprobar" },
    { to: "/compras/ordenes", label: "3 · Órdenes de compra y recepciones", cap: "compras_gestion" },
    { to: "/compras/facturas", label: "4-6 · Facturas y pagos", anyCap: ["compras_gestion", "compras_directa"] },
    { heading: "Seguimiento de aprobación" },
    { to: "/compras/kpis", label: "Panel de aprobación", cap: "compras_aprobar" },
    { heading: "Reportes" },
    { to: "/compras/reporte-pagos", label: "Pagos de requisiciones", anyCap: ["compras_gestion", "compras_aprobar", "compras_directa"], soloGerencia: true },
    { to: "/compras/auditoria-requisiciones", label: "Auditoría de requisiciones", anyCap: ["compras_gestion", "compras_aprobar", "compras_directa"] },
    { heading: "Notas de crédito (Panamá)" },
    { to: "/compras/nc", label: "Notas de crédito", feature: "compras_nc_gestion" },
    { heading: "Organización de despachos" },
    { to: "/etiquetas", label: "Etiquetas", cap: "etiquetas_packing", feature: "etiquetas_packing", hideReadonly: true },
    { to: "/compras/oficina", label: "Listo para despacho", cap: "compras_gestion" },
    { to: "/compras/almacen", label: "Almacén y contenedores", cap: "compras_gestion" },
  ]},
  // Usuario SOLO de consulta de lotes NC (Panamá/GD) que no ve el menú Compras:
  // entrada propia. Quien ya ve Compras llega por su submenú.
  { to: "/compras/nc", label: "Lotes NC (Panamá)", feature: "compras_nc_consulta_solo" },
  // Portal personal de ITAM ("Mis recursos") vive en el menú del avatar,
  // debajo de Perfil (ver AvatarMenu). La API limita a la propia custodia.
  { to: "/itam/kpis", label: "Activos", k: "nav.itam", cap: "itam", feature: "itam", children: [
    { heading: "Agregar" },
    { to: "/itam/activos/nuevo", label: "Nuevo activo", cap: "itam_gestion", feature: "itam" },
    { to: "/itam/levantamientos", label: "Levantamiento de inventario", cap: "itam_gestion", feature: "itam" },
    { heading: "Consultar" },
    { to: "/itam/kpis", label: "Indicadores (KPIs)", cap: "itam", feature: "itam" },
    { to: "/itam/activos", label: "Listado de activos", cap: "itam", feature: "itam" },
    { to: "/itam/solicitudes", label: "Solicitudes internas", cap: "itam_gestion", feature: "itam" },
    { to: "/itam/ubicaciones", label: "Ubicaciones", cap: "itam_admin", feature: "itam" },
    { to: "/itam/gabinetes", label: "Gabinetes / Racks (posiciones)", cap: "itam", feature: "itam" },
    { heading: "Reportes" },
    { to: "/itam/reportes/ubicaciones", label: "Por ubicación", cap: "itam", feature: "itam" },
    { to: "/itam/reportes/retirados", label: "Retirados / dados de baja", cap: "itam", feature: "itam" },
    { to: "/itam/reportes/revisiones", label: "Revisiones por jornada", cap: "itam", feature: "itam" },
    { to: "/itam/contratos", label: "Contratos y garantías", cap: "itam", feature: "itam" },
    { to: "/itam/licencias", label: "Licencias de software", cap: "itam", feature: "itam" },
    { to: "/a", label: "Consulta pública por QR (compartir/celular)", cap: "itam", feature: "itam" },
    { heading: "Configuración" },
    { to: "/itam/catalogos", label: "Catálogos (categorías, marcas, modelos)", cap: "itam_gestion", feature: "itam" },
  ]},
  { to: "/tablero-gerencial", label: "Reportes", k: "nav.reports", feature: "reportes", reportsMenu: true, children: [
    { heading: "Ejecutivo" },
    { to: "/tablero-gerencial", label: "Tablero Gerencial", cap: "tablero_gerencial" },
    { to: "/plan-apertura", label: "Plan de Apertura", k: "rep.planapertura", anyCap: ["dashboard", "plan_apertura_pais"], report: "plan_apertura", feature: "arquitectura" },
    { to: "/zoho-stats", label: "Estadísticas Zoho Desk", cap: "zoho_stats", report: "zoho_stats", hideFeature: "zoho_off" },
    { heading: "Generar informes" },
    { to: "/proyectos-activos", label: "Estado actual de proyectos", k: "rep.projstatus", cap: "dashboard", report: "estado_proyectos" },
    { to: "/acta-avances", label: "Acta de avances / entrega", cap: "dashboard", report: "acta_avances" },
    { to: "/informe-proyectos", label: "Resumen Proyectos", k: "rep.projreport", cap: "export_data", report: "informe_proyectos" },
    { to: "/requisiciones-proyecto", label: "Inventario solicitado por proyecto", k: "rep.reqproj", cap: "export_data", report: "requisiciones_proyecto" },
    { heading: "Proyectos y tareas" },
    { to: "/gestion-tareas", label: "Gestión de tareas", k: "rep.taskmgmt", cap: "metrics", report: "gestion_tareas" },
    { to: "/actividad-usuarios", label: "Actividad por usuario", k: "rep.useractivity", cap: "metrics", report: "actividad_usuarios" },
    { to: "/tiempo-por-miembro", label: "Tiempo Invertido", cap: "metrics", report: "tiempo_por_miembro" },
    { to: "/reporte-tareas", label: "Reporte de tareas", k: "rep.taskreport", cap: "dashboard", report: "reporte_tareas" },
    { heading: "Análisis y métricas", cap: "metrics" },
    { to: "/metricas", label: "Velocidad y análisis", k: "nav.metrics", cap: "metrics", report: "velocidad", hideFeature: "metricas_off" },
    { to: "/cfd", label: "Flujo acumulativo (CFD)", cap: "metrics", report: "cfd", hideFeature: "metricas_off" },
    { heading: "Country Manager", cap: "envios", feature: "envios" },
    { to: "/compras/rastreo-envios", label: "Seguimiento de envíos", cap: "envios", feature: "envios" },
  ]},
  { to: "/documentacion", label: "Herramientas", k: "nav.tools", children: [
    { to: "/documentacion", label: "Documentación", k: "nav.docs", cap: "documents", feature: "documentacion" },
    { heading: "Levantamiento", accent: "cyan", cap: "levantamiento", feature: "levantamiento" },
    { to: "/levantamiento", label: "Levantamientos", cap: "levantamiento", feature: "levantamiento", accent: "cyan" },
    { to: "/levantamiento/cuentas", label: "Cuentas (clientes y oportunidades)", cap: "levantamiento", feature: "levantamiento", accent: "cyan" },
    { to: "/levantamiento/enlaces", label: "Actas de entrega (enlace público)", cap: "levantamiento", feature: "levantamiento", accent: "cyan" },
    { to: "/levantamiento/catalogos", label: "Catálogos", cap: "levantamiento_admin", feature: "levantamiento", accent: "cyan" },
    { heading: "Calendario", cap: "calendario", feature: "calendario", hideCM: true },
    { to: "/calendario", label: "Calendario de eventos", cap: "calendario", feature: "calendario", hideCM: true },
  ]},
  // Tiendas en línea (Medusa + conector ICG). Va al FINAL, después de
  // Herramientas (pedido 2026-11-04). `feature` lo oculta si el módulo está
  // apagado; `anyCap` lo enseña a quien tenga cualquiera de las tres capacidades.
  { to: "/tiendas", label: "Tiendas en línea", k: "nav.tiendas", modulo: "tiendas", feature: "tiendas",
    anyCap: ["tiendas", "tiendas_gestion", "tiendas_sync"] },
];

// Módulos cuya letra va un punto más pequeña (fuente compacta −10%). Los
// prefijos de ruta se derivan del propio menú, así no se desactualizan si se
// agregan pantallas nuevas a esos módulos. El menú superior siempre es compacto.
const COMPACT_MENUS = new Set(["Compras", "Activos", "Reportes", "Herramientas", "Soporte"]);
const COMPACT_PREFIXES = NAV
  .filter((it) => COMPACT_MENUS.has(it.label))
  .flatMap((it) => [it.to, ...(it.children || []).map((c) => c.to)])
  .filter(Boolean)
  .concat(["/soporte", "/levantamiento"]);   // por si llegan subrutas sueltas
function rutaCompacta(path) {
  return COMPACT_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

// Items that live under the settings menu instead of the main nav.
// `g` agrupa por función (panel de control) para el desplegable de Ajustes.
const SETTINGS = [
  { to: "/admin/usuarios", label: "Usuarios", icon: "users", cap: "manage_users", g: "Usuarios y perfiles" },
  { to: "/admin/departamentos", label: "Departamentos", icon: "building", cap: "manage_departments", g: "Usuarios y perfiles" },
  { to: "/admin/permisos", label: "Permisos por perfil", icon: "shield", cap: "manage_config", g: "Usuarios y perfiles" },
  { to: "/admin/perfiles", label: "Perfiles personalizados", icon: "users", cap: "manage_config", g: "Usuarios y perfiles" },
  { to: "/admin/estados", label: "Estados por equipo", icon: "list", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/etiquetas", label: "Etiquetas (país/marca)", icon: "star", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/campos", label: "Campos personalizados", icon: "sheet", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/tipos-proyecto", label: "Tipos de proyecto", icon: "folder", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/proyectos-eliminados", label: "Proyectos eliminados", icon: "refresh", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/plantillas-materiales", label: "Plantillas de materiales", icon: "list", cap: "manage_config", g: "Estructura de datos", feature: "arquitectura" },
  { to: "/admin/plantillas-checklist", label: "Plantillas de checklist", icon: "checkSquare", cap: "manage_config", g: "Estructura de datos" },
  { to: "/admin/workflows", label: "Flujos de trabajo", icon: "zap", cap: "manage_config", g: "Estructura de datos" },
  { to: "/compras/presupuesto", label: "Partidas presupuestarias", icon: "receipt", cap: "compras_aprobar", g: "Estructura de datos", feature: "compras" },
  { to: "/compras/catalogos", label: "Catálogos de compras", icon: "list", anyCap: ["compras_gestion", "compras_directa"], g: "Estructura de datos", feature: "compras" },
  { to: "/admin/reglas-aprobacion", label: "Reglas de aprobación", icon: "shield", cap: "manage_config", g: "Estructura de datos", feature: "compras" },
  { to: "/datos", label: "Importar / Exportar", icon: "download", cap: "export_data", g: "Datos y comunicación" },
  { to: "/directorio", label: "Directorio de correos", icon: "mail", cap: "manage_contacts", feature: "directorio", g: "Datos y comunicación" },
  { to: "/configuracion", label: "Configuración", icon: "settings", cap: "manage_config", g: "Sistema" },
  { to: "/bitacora", label: "Bitácora", icon: "history", cap: "bitacora", g: "Sistema" },
  { to: "/diagnostico", label: "Diagnóstico", icon: "shield", cap: "manage_config", g: "Sistema" },
];
const SETTINGS_GROUPS = ["Usuarios y perfiles", "Estructura de datos", "Datos y comunicación", "Sistema"];

function useClickOutside(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  return ref;
}

const NOTIF_ICON = { assigned: "pin", blocked: "ban", comment: "message", shared: "share", due_soon: "clock", mention: "user", follow: "star" };

function NotificationsBell() {
  const { items, unread, markRead, markAll } = useNotifications();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();
  const ref = useClickOutside(() => setOpen(false));
  const visibleItems = (items || []).filter((n) => filter === "unread" ? !n.is_read : filter === "messages" ? n.origin === "chat" || (n.link || "").startsWith("/mensajeria") : true);

  const openNotif = (n) => {
    markRead(n.id);
    setOpen(false);
    if (n.link) nav(n.link);
    else if (n.task_id) nav(`/?task=${n.task_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Notificaciones">
        <Icon name="bell" size={19} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-1rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <span className="text-sm font-semibold">{t("top.notifications")}</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-brand-600 hover:underline">{t("top.markAllRead")}</button>}
          </div>
          <div className="flex gap-1 border-b border-slate-100 px-3 py-2">
            {[["all", "Todas"], ["unread", "No leídas"], ["messages", "Mensajes"]].map(([key, label]) => <button key={key} onClick={() => setFilter(key)} className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${filter === key ? "bg-brand-100 text-brand-700" : "text-slate-500 hover:bg-slate-50"}`}>{label}</button>)}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {visibleItems.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">{filter === "all" ? t("top.noNotifications") : "No hay notificaciones en esta vista."}</p>}
            {visibleItems.map((n) => (
              <button key={n.id} onClick={() => openNotif(n)}
                className={`flex w-full items-start gap-2.5 border-b border-slate-50 px-4 py-2.5 text-left hover:bg-slate-50 ${n.is_read ? "" : "bg-brand-50/40"}`}>
                <Icon name={NOTIF_ICON[n.type] || "bell"} size={16} className="mt-0.5 text-brand-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="truncate text-sm font-medium">{n.title}</span>
                    {!n.is_read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
                  </div>
                  {n.body && <p className="truncate text-xs text-slate-500">{n.body}</p>}
                  <p className="text-[11px] text-slate-400">{fmtStamp(n.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); nav("/actividad"); }}
            className="block w-full border-t border-slate-100 px-4 py-2.5 text-center text-xs font-medium text-brand-600 hover:bg-slate-50">
            {t("top.viewAllActivity")}
          </button>
        </div>
      )}
    </div>
  );
}

// Aviso de WhatsApp en el header: muestra si hay mensajes de cliente pendientes
// en los grupos monitoreados (y grupos sin asociar). Lleva al triage.
function WhatsAppBell() {
  const nav = useNavigate();
  const [pend, setPend] = useState(0);
  const [sinAsoc, setSinAsoc] = useState(0);
  useEffect(() => {
    let vivo = true;
    const load = () => api.get("/soporte/whatsapp/triage/pendientes-count")
      .then((r) => { if (vivo) { setPend(r.data.pendientes || 0); setSinAsoc(r.data.sin_asociar || 0); } })
      .catch(() => {});
    load();
    const h = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { vivo = false; clearInterval(h); window.removeEventListener("focus", onFocus); };
  }, []);
  const total = pend;
  const title = `WhatsApp — ${pend} mensaje(s) por triar${sinAsoc ? ` · ${sinAsoc} grupo(s) sin asociar` : ""}`;
  // Con mensajes pendientes se vuelve un anuncio VERDE bien visible; sin nada,
  // queda como un botón discreto.
  if (total > 0) {
    return (
      <button onClick={() => nav("/soporte/whatsapp")} title={title}
        className="flex items-center gap-1.5 rounded-tag border border-emerald-300 bg-emerald-500 px-2.5 py-1 text-white transition hover:bg-emerald-600">
        <Icon name="message" size={16} className="text-white" />
        <span className="text-[12px] font-bold tabular-nums">{total > 99 ? "99+" : total}</span>
      </button>
    );
  }
  return (
    <button onClick={() => nav("/soporte/whatsapp")} className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100" title={title}>
      <Icon name="message" size={18} />
    </button>
  );
}

// Barra inferior de acceso rápido para agentes en MÓVIL (el menú de arriba se
// desborda en pantallas chicas). Accesos: Mesa de soporte, Tickets, Nuevo ticket
// y WhatsApp (con su badge de pendientes).
function MobileSoporteNav() {
  const nav = useNavigate();
  const loc = useLocation();
  const [waPend, setWaPend] = useState(0);
  useEffect(() => {
    let vivo = true;
    const load = () => api.get("/soporte/whatsapp/triage/pendientes-count")
      .then((r) => { if (vivo) setWaPend(r.data.pendientes || 0); }).catch(() => {});
    load();
    const h = setInterval(load, 25000);
    return () => { vivo = false; clearInterval(h); };
  }, []);
  const p = loc.pathname;
  const items = [
    { k: "mesa", label: "Mesa", icon: "ticket", act: () => nav("/soporte"), on: p === "/soporte" },
    { k: "tickets", label: "Tickets", icon: "list", act: () => nav("/soporte/tickets"), on: p.startsWith("/soporte/tickets") },
    { k: "nuevo", label: "Nuevo", icon: "plus", act: () => nav("/soporte/tickets", { state: { nuevo: true } }), centro: true },
    { k: "wa", label: "WhatsApp", icon: "message", act: () => nav("/soporte/whatsapp"), on: p === "/soporte/whatsapp", badge: waPend },
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(15,23,42,.10)] sm:hidden">
      {items.map((it) => (
        <button key={it.k} onClick={it.act}
          className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-semibold ${it.on ? "text-brand-600" : "text-slate-400"}`}>
          {it.centro
            ? <span className="-mt-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg"><Icon name="plus" size={22} /></span>
            : <Icon name={it.icon} size={20} className={it.on ? "text-brand-600" : ""} />}
          <span>{it.label}</span>
          {it.badge > 0 && <span className="absolute right-4 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[11px] font-bold text-white">{it.badge > 9 ? "9+" : it.badge}</span>}
        </button>
      ))}
    </nav>
  );
}

const SETTINGS_GROUP_KEY = {
  "Usuarios y perfiles": "set.g.users", "Estructura de datos": "set.g.data",
  "Datos y comunicación": "set.g.comm", "Sistema": "set.g.system",
};

function SettingsMenu({ dropUp = false }) {
  const { can, user, roleAtLeast, features, nivelRuta } = useAuth();
  const { departments = [] } = useMeta();
  const t = useT();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useClickOutside(() => setOpen(false));
  // Plantillas de materiales/checklist: también Arquitectura / División Front y coordinadores.
  const deptName = (departments.find((d) => d.id === user?.department_id)?.name || "").toLowerCase();
  const canTemplates = can("manage_config") || roleAtLeast("supervisor") || deptName.includes("arquitect") || deptName.includes("front");
  const items = SETTINGS.filter((s) => {
    // Perfil de árbol: un submenú Oculto no aparece aunque la cap lo permita.
    if (nivelRuta(s.to) === 0) return false;
    if (s.to === "/admin/plantillas-materiales") return canTemplates && features?.arquitectura;
    if (s.to === "/admin/plantillas-checklist") return canTemplates;
    if (s.feature && !features?.[s.feature]) return false;
    if (s.anyCap) return s.anyCap.some((c) => can(c));
    return can(s.cap);
  });
  if (items.length === 0) return null;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title={t("top.settings")}>
        <Icon name="settings" size={19} />
      </button>
      {open && (
        <div className={`absolute z-50 max-h-[80vh] w-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-pop ${dropUp ? "bottom-full left-0 mb-2" : "right-0 mt-2"}`}>
          {SETTINGS_GROUPS.map((g) => {
            const inGroup = items.filter((s) => s.g === g);
            if (inGroup.length === 0) return null;
            return (
              <div key={g}>
                <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">{t(SETTINGS_GROUP_KEY[g], g)}</div>
                {inGroup.map((s) => (
                  <button key={s.to} onClick={() => { setOpen(false); nav(s.to); }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm hover:bg-slate-50">
                    <Icon name={s.icon} size={16} className="text-slate-400" />
                    {/* Marcas apagadas en Configuración → Preferencias de la plataforma (empresa.usa_marca). */}
                    {s.to === "/admin/etiquetas" && features?.usa_marca === false ? "Etiquetas (país)" : s.label}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AvatarMenu({ dropUp = false, showDetails = false }) {
  const { user, logout, features } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useClickOutside(() => setOpen(false));
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} title={showDetails ? undefined : user.full_name}
        className={`flex items-center gap-2 rounded-lg hover:bg-slate-100 ${showDetails ? "w-full justify-start p-2 text-left" : "p-1"}`}>
        <Avatar name={user.full_name} src={user.avatar_url} size={32} />
        {showDetails && <span className="min-w-0 flex-1"><span className="block truncate text-xs font-semibold text-slate-700">{user.full_name}</span><span className="block truncate text-[10px] text-slate-500">{ROLE_LABEL[user.role]}</span></span>}
        {showDetails && <Icon name="chevron-down" size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className={`absolute z-50 max-h-[80vh] w-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl ${dropUp ? "bottom-full left-0 mb-2" : "right-0 mt-2"}`}>
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-semibold">{user.full_name}</div>
            <div className="text-xs text-slate-400">{ROLE_LABEL[user.role]}</div>
          </div>
          <button onClick={() => { setOpen(false); nav("/perfil"); }} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm hover:bg-slate-50"><Icon name="user" size={16} className="text-slate-400" />{t("top.profile")}</button>
          {features?.itam && <button onClick={() => { setOpen(false); nav("/mis-recursos"); }} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm hover:bg-slate-50"><Icon name="laptop" size={16} className="text-slate-400" />Mis recursos</button>}
          <button onClick={() => { logout(); nav("/login"); }} className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50"><Icon name="logout" size={16} />{t("top.logout")}</button>
        </div>
      )}
    </div>
  );
}

/** Contadores que se pintan junto a una entrada del menú. */
const BadgeCtx = createContext({});

/** Píldora con el número de pendientes; no se pinta si no hay ninguno. */
function Badge({ nombre }) {
  const conteos = useContext(BadgeCtx);
  const n = conteos[nombre] || 0;
  if (!n) return null;
  return (
    <span className="ml-auto rounded-tag bg-amber-500 px-1.5 text-[11px] font-bold text-white">{n}</span>
  );
}

function NavDropdown({ item }) {
  // En pruebas: se ve, no se entra.
  if (item.bloqueado) {
    return (
      <span title="Módulo en pruebas · por ahora solo lo usan los administradores"
        className="flex cursor-not-allowed items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-300">
        {item.label}
        <Icon name="lock" size={12} />
      </span>
    );
  }
  return <NavDropdownAbierto item={item} />;
}

function NavDropdownAbierto({ item }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();
  const isActive = item.children.some((c) => loc.pathname === c.to) || loc.pathname === item.to;
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      {/* La etiqueta navega a la página principal del grupo; el chevron abre el
          submenú. Así "Proyectos" entra a /proyectos y sigue ofreciendo Timeline. */}
      <div className={`flex items-center whitespace-nowrap rounded-lg transition ${isActive ? moduloDe(item.to) : "text-tinta-2 hover:bg-relleno"}`}>
        <NavLink to={item.to} end={item.end} onClick={() => setOpen(false)}
          className="flex flex-col items-center gap-0.5 rounded-l-lg py-1.5 pl-2.5 pr-1 xl:pl-3.5">
          <Icon name={iconoDe(item.to)} size={15} />
          <span className="text-nav font-semibold">{t(item.k, item.label)}</span>
        </NavLink>
        {/* Divisor vertical muy sutil: separa la zona que NAVEGA (nombre) de la
            que ABRE el submenú (flecha). El leve hover propio refuerza que la
            flecha es un control aparte. Pedido 2026-09-23. */}
        <button onClick={() => setOpen((o) => !o)} aria-label="Abrir submenú" title="Abrir submenú"
          className="my-1.5 self-stretch rounded-r-lg border-l border-slate-400/30 pl-1 pr-2 transition hover:bg-slate-500/10 xl:pr-2.5">
          <Icon name="chevron-down" size={12} className={`opacity-70 transition ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[210px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {item.children.map((c) => c.heading ? (
            <div key={`h-${c.heading}`} className={`mt-1 border-t border-slate-100 px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide first:mt-0 first:border-0 first:pt-1 ${c.accent === "cyan" ? "text-cyan-600" : "text-brand-600"}`}>
              {c.heading}
            </div>
          ) : (
            <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)}
              className={({ isActive: a }) => `flex items-center gap-2 px-4 py-2 text-sm transition ${a ? (c.accent === "cyan" ? "bg-cyan-50 text-cyan-700 font-medium" : "bg-brand-50 text-brand-700 font-medium") : "text-slate-600 hover:bg-slate-50"}`}>
              {t(c.k, c.label)}
              {c.badge && <Badge nombre={c.badge} />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function EnterpriseSidebar({ items, collapsed, location, t }) {
  const [expanded, setExpanded] = useState({});
  // Con la barra COMPRIMIDA los submenús no caben; se muestran como un panel
  // flotante (flyout) al pasar el mouse por el ítem (pedido 2026-11-04). Es
  // `position:fixed` para escapar el `overflow-y-auto` del nav. `flyout` = el
  // ítem bajo el cursor + la coordenada vertical de su fila.
  const [flyout, setFlyout] = useState(null);
  const flyoutTimer = useRef();
  const abrirFlyout = (to, el) => { clearTimeout(flyoutTimer.current); setFlyout({ to, top: el.getBoundingClientRect().top }); };
  const cerrarFlyout = () => { clearTimeout(flyoutTimer.current); flyoutTimer.current = setTimeout(() => setFlyout(null), 140); };
  const isRouteActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);
  const groupActive = (item) => isRouteActive(item.to) || item.children?.some((child) => child.to && isRouteActive(child.to));

  return (
    <aside className={`fixed bottom-0 left-0 top-16 z-20 hidden shrink-0 flex-col border-r border-slate-200 transition-[width] duration-200 lg:flex ${collapsed ? "w-[72px]" : "w-64"}`} style={{ backgroundColor: "var(--tf-sidebar-bg, #fff)" }}>
      <nav aria-label="Navegación principal" className={`min-h-0 flex-1 overflow-y-auto py-3 ${collapsed ? "px-2" : "px-3"}`}>
        {!collapsed && <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[.08em] text-slate-400">Espacio de trabajo</div>}
        {items.map((item) => {
          const active = groupActive(item);
          const open = !collapsed && (expanded[item.to] ?? active);
          const icon = iconoDe(item.to);
          const label = t(item.k, item.label);
          const rowClass = `group flex min-h-10 items-center gap-2.5 rounded-lg px-2.5 text-[13px] transition-colors ${active ? `${moduloDe(item.to)} font-semibold` : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"} ${collapsed ? "justify-center px-0" : ""}`;
          return (
            <div key={item.to} className="mb-0.5"
              onMouseEnter={collapsed && item.children ? (e) => abrirFlyout(item.to, e.currentTarget) : undefined}
              onMouseLeave={collapsed && item.children ? cerrarFlyout : undefined}>
              <div className="flex items-center gap-1">
                {item.bloqueado ? (
                  <span className={`${rowClass} flex-1 cursor-not-allowed opacity-60`} title={item.bloqueado ? `${label} · módulo restringido` : label}>
                    <Icon name={icon} size={17} /><span className={collapsed ? "sr-only" : "min-w-0 flex-1 truncate"}>{label}</span>
                  </span>
                ) : (
                  <NavLink to={item.to} end={item.end} title={collapsed ? label : undefined}
                    onClick={() => { if (item.children) setExpanded((previous) => ({ ...previous, [item.to]: true })); }}
                    className={`${rowClass} ${collapsed ? "flex-1" : "min-w-0 flex-1"}`}>
                    <Icon name={icon} size={17} /><span className={collapsed ? "sr-only" : "min-w-0 flex-1 truncate"}>{label}</span>
                  </NavLink>
                )}
                {!collapsed && item.children && (
                  <button type="button" aria-label={`${open ? "Contraer" : "Expandir"} ${label}`} aria-expanded={open}
                    onClick={() => setExpanded((previous) => ({ ...previous, [item.to]: !open }))}
                    className="flex h-8 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-brand-500">
                    <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
                  </button>
                )}
                {collapsed && item.children && <span className="sr-only">{item.children.length} opciones</span>}
              </div>
              {collapsed && item.children && flyout?.to === item.to && (
                <div className="fixed z-40" style={{ left: 72, top: flyout.top }}
                  onMouseEnter={() => clearTimeout(flyoutTimer.current)} onMouseLeave={cerrarFlyout}>
                  <div className="max-h-[70vh] w-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                    <div className="px-2 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
                    {item.children.map((child, index) => child.heading ? (
                      <div key={`fh-${item.to}-${index}`} className={`px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide ${child.accent === "cyan" ? "text-cyan-700" : "text-slate-400"}`}>
                        {t(child.k, child.heading)}
                      </div>
                    ) : (
                      <NavLink key={child.to} to={child.to} end={child.end} onClick={() => setFlyout(null)}
                        className={({ isActive }) => `flex min-h-8 items-center gap-1.5 rounded-md px-2 text-[12px] transition-colors ${isActive ? "bg-blue-50 font-semibold text-blue-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                        <span className="min-w-0 flex-1 truncate">{t(child.k, child.label)}</span>
                        {child.badge && <Badge nombre={child.badge} />}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
              {open && !item.bloqueado && item.children && (
                <div className="ml-4 mt-1 border-l border-slate-200 pl-2">
                  {item.children.map((child, index) => child.heading ? (
                    <div key={`heading-${item.to}-${index}`} className={`px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide ${child.accent === "cyan" ? "text-cyan-700" : "text-slate-400"}`}>
                      {t(child.k, child.heading)}
                    </div>
                  ) : (
                    <NavLink key={child.to} to={child.to} end={child.end}
                      className={({ isActive }) => `flex min-h-8 items-center gap-1.5 rounded-md px-2 text-[12px] transition-colors ${isActive ? "bg-blue-50 font-semibold text-blue-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                      <span className="min-w-0 flex-1 truncate">{t(child.k, child.label)}</span>
                      {child.badge && <Badge nombre={child.badge} />}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className={`flex shrink-0 items-center border-t border-slate-100 py-2.5 ${collapsed ? "justify-center gap-1 px-2" : "gap-1.5 px-3"}`}>
        <div className={collapsed ? "" : "min-w-0 flex-1"}>
          <AvatarMenu dropUp showDetails={!collapsed} />
        </div>
        <SettingsMenu dropUp />
      </div>
    </aside>
  );
}

// Ayuda de atajos de teclado (se abre con "?").
function ShortcutsHelp({ onClose }) {
  const rows = [
    ["p", "Proyectos"], ["m", "Mensajería"], ["i", "Mis Tareas (inicio)"],
    ["t", "Tablero del departamento"], ["a", "Actividad"], ["d", "Documentación"],
    ["c", "Crear tarea"], ["/", "Buscar en la página"], ["Ctrl/Cmd + K", "Buscador global"],
    ["↑ / ↓", "Ordenar proyectos ascendente / descendente"], ["?", "Mostrar esta ayuda"],
  ];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="card w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Atajos de teclado</h3>
          <button className="btn-ghost px-2" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="space-y-1.5">
          {rows.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{label}</span>
              <kbd className="rounded border border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-700">{k}</kbd>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Los atajos se ignoran mientras escribes en un campo.</p>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, can, readonly, features, hasReport, nivelRuta } = useAuth();
  const t = useT();
  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const loadMessages = () => api.get("/messaging/conversations").then((r) => {
      if (active) setMsgUnread((r.data.conversations || []).reduce((sum, c) => sum + (Number(c.unread) || 0), 0));
    }).catch(() => {});
    loadMessages();
    const timer = setInterval(loadMessages, 30000);
    window.addEventListener("tf:notification", loadMessages);
    window.addEventListener("tf:notifications-refresh", loadMessages);
    return () => { active = false; clearInterval(timer); window.removeEventListener("tf:notification", loadMessages); window.removeEventListener("tf:notifications-refresh", loadMessages); };
  }, [user]);
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileNav, setMobileNav] = useState(false);
  const [navLayout, setNavLayoutState] = useState("standard");
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const crearRef = useRef(null);
  useEffect(() => {
    if (!crearOpen) return undefined;
    const fuera = (e) => { if (!crearRef.current?.contains(e.target)) setCrearOpen(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [crearOpen]);
  const [maint, setMaint] = useState(null);
  useEffect(() => {
    if (!user?.id) return undefined;
    setNavLayoutState(getNavLayout(user.id));
    setSidebarCollapsedState(getSidebarCollapsed(user.id));
    const onLayoutChange = (event) => {
      if (event.detail?.userId === user.id) setNavLayoutState(event.detail.layout === "erp" ? "erp" : "standard");
    };
    window.addEventListener("tf:nav-layout-change", onLayoutChange);
    return () => {
      window.removeEventListener("tf:nav-layout-change", onLayoutChange);
    };
  }, [user?.id]);
  // Pendientes de firmar, para el contador del menú. Se refresca cada 2 min y
  // al cambiar de página (así baja solo en cuanto apruebas algo).
  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (!can("compras") || !features?.compras) return undefined;
    let alive = true;
    const traer = () => api.get("/compras/aprobaciones/resumen")
      .then((r) => { if (alive) setBadges({ compras_aprobaciones: r.data?.pendientes || 0 }); })
      .catch(() => {});
    traer();
    const id = setInterval(traer, 120000);
    return () => { alive = false; clearInterval(id); };
  }, [can, features, loc.pathname]);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/auth/maintenance").then((r) => { if (alive) { setMaint(r.data); if (r.data?.app_tz) applyAppTimezone(r.data.app_tz); } }).catch(() => {});
    load();
    const t = setInterval(load, 60000);   // refresca por si se activa/desactiva mientras trabajas
    return () => { alive = false; clearInterval(t); };
  }, []);

  const [fabOpen, setFabOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);
  const [spotlight, setSpotlight] = useState(false);   // buscador global (C3)
  const fabRef = useClickOutside(() => setFabOpen(false));
  useEffect(() => { requestBrowserPermission(); }, []);
  useEffect(() => {
    const h = () => setShowHelp((v) => !v);
    window.addEventListener("tf:shortcuts-help", h);
    return () => window.removeEventListener("tf:shortcuts-help", h);
  }, []);
  // Ctrl/Cmd+K abre el buscador global (Spotlight). Se maneja aparte porque el
  // handler de atajos ignora las combinaciones con Ctrl/Cmd.
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSpotlight((v) => !v);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });

  // Guardia GLOBAL contra el salto de scroll: en Safari/iOS no existe
  // overflow-anchor (CSS), así que ningún parche por función bastó (reporte
  // recurrente). Una sola corrección a 2 frames del clic no alcanza cuando el
  // reflow llega más tarde (un fetch que resuelve y agrega una fila, un
  // autofocus que el navegador desplaza solo) — así que mientras dura la
  // "ventana" tras cada clic/cambio/foco, cualquier mutación del DOM o evento
  // de scroll vuelve a fijar la posición capturada. Los controles que sí
  // quieren mover el scroll a propósito (ir arriba/abajo…) se marcan con
  // data-scroll-intent para quedar exentos.
  useEffect(() => {
    const el = document.scrollingElement || document.documentElement;
    let target = null;
    let venceTimer = null;
    const restaurar = () => {
      if (target != null && el.scrollTop !== target) el.scrollTop = target;
    };
    const mo = new MutationObserver(() => {
      if (target != null) requestAnimationFrame(restaurar);
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true });
    const onScroll = () => { if (target != null) requestAnimationFrame(restaurar); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const arm = (e) => {
      if (e.target?.closest?.("[data-scroll-intent]")) return;
      target = el.scrollTop;
      clearTimeout(venceTimer);
      venceTimer = setTimeout(() => { target = null; }, 700);
      requestAnimationFrame(restaurar);
    };
    document.addEventListener("click", arm, true);
    document.addEventListener("change", arm, true);
    // "input" (no solo "change"): en un campo de texto, "change" recién dispara
    // al perder el foco — mientras escribes/filtras (buscadores de ítems,
    // proveedores…) React usa "input" en cada tecla, y ESO es lo que reordena
    // la lista/desplegable y provoca el salto. Sin esto, la ventana de
    // protección se vencía a los 700ms de haber enfocado el campo, antes de
    // que terminaras de escribir.
    document.addEventListener("input", arm, true);
    document.addEventListener("focusin", arm, true);
    document.addEventListener("keydown", arm, true);
    return () => {
      mo.disconnect();
      clearTimeout(venceTimer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", arm, true);
      document.removeEventListener("input", arm, true);
      document.removeEventListener("keydown", arm, true);
      document.removeEventListener("change", arm, true);
      document.removeEventListener("focusin", arm, true);
    };
  }, []);

  // Create where the user is looking: on the board, create in-place (so it
  // refreshes that board); otherwise go to Mis Tareas and open the form.
  const createTask = () => {
    if (loc.pathname === "/departamento") {
      window.dispatchEvent(new Event("tf:create-task"));
    } else {
      nav("/");
      setTimeout(() => window.dispatchEvent(new Event("tf:create-task")), 60);
    }
  };

  // El alta de proyecto la abre la propia pantalla de Proyectos al oír el
  // evento; si no estamos ahí, primero se navega.
  const crearProyecto = () => {
    if (loc.pathname === "/proyectos") {
      window.dispatchEvent(new Event("tf:create-project"));
    } else {
      nav("/proyectos");
      setTimeout(() => window.dispatchEvent(new Event("tf:create-project")), 60);
    }
  };

  // Global keyboard shortcuts — ignored while typing in a field, so they never
  // interfere with normal text entry (see Mi Perfil → Atajos de teclado).
  useEffect(() => {
    const handler = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.isContentEditable) return;
      // Flechas ↑/↓: orden ascendente / descendente en la lista de proyectos.
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        if (loc.pathname === "/proyectos") {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent("tf:sort-dir", { detail: e.key === "ArrowUp" ? "asc" : "desc" }));
        }
        return;
      }
      const key = e.key.toLowerCase();
      if (key === "c" && !readonly) { e.preventDefault(); createTask(); }
      else if (key === "/") { e.preventDefault(); document.getElementById("tf-page-search")?.focus(); }
      else if (key === "p") { e.preventDefault(); nav("/proyectos"); }
      else if (key === "m") { e.preventDefault(); nav("/mensajeria"); }
      else if (key === "i") { e.preventDefault(); nav("/"); }
      else if (key === "t") { e.preventDefault(); nav("/departamento"); }
      else if (key === "a") { e.preventDefault(); nav("/actividad"); }
      else if (key === "d") { e.preventDefault(); nav("/documentacion"); }
      else if (key === "?") { e.preventDefault(); window.dispatchEvent(new Event("tf:shortcuts-help")); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [loc.pathname, readonly]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  // "Tablero de dirección" solo para coordinadores/gerentes (no member/pmo).
  const directorRoles = ["supervisor", "manager", "admin", "directivo"];
  const isDirector = directorRoles.includes(user.role) || directorRoles.includes(user.secondary_role);
  // Solo Dirección (directivo/admin): el presupuesto/partidas es información suya.
  const esDirectivo = ["directivo", "admin"].includes(user.role) || ["directivo", "admin"].includes(user.secondary_role);
  // Gerencia (acceso al reporte de Pagos de requisiciones): gerente, directivo/CFO, admin, country_manager.
  const gerenciaRoles = ["manager", "directivo", "admin", "country_manager"];
  const esGerencia = gerenciaRoles.includes(user.role) || gerenciaRoles.includes(user.secondary_role);
  const items = NAV.filter((n) => {
    if (readonly && n.hideReadonly) return false;
    // Un módulo APAGADO desaparece para todos. Uno encendido «solo para
    // administradores» sigue a la vista pero desactivado: así el resto sabe
    // que existe y que aún no le toca, en vez de que aparezca de la nada.
    if (n.feature && !features?.[n.feature] && !features?.[`${n.feature}_bloqueado`]) return false;
    // Perfil de árbol: un menú sin hijos (p. ej. Tiendas) marcado Oculto no se ve.
    if (!n.children && nivelRuta(n.to) === 0) return false;
    if (readonly && n.alsoReadonly) return true;   // Proyectos visible para Directivo
    // `anyCap`: visible si el rol tiene CUALQUIERA de esas capacidades (p. ej.
    // Compras se ve con acceso base, gestión O aprobación).
    if (n.anyCap) return n.anyCap.some(can);
    return n.cap ? can(n.cap) : true;
  }).map((n) => (
    n.feature && features?.[`${n.feature}_bloqueado`] ? { ...n, bloqueado: true } : n
  )).map((n) => {
    // Los hijos con cap propia se ocultan si el rol no la tiene.
    // Un reporte concedido puntualmente (c.report + hasReport) se ve aunque no
    // se tenga la capacidad base ni el rol de director.
    if (!n.children) return n;
    const visible = (c) => {
      // Perfil de árbol (Fase 3b): el nivel del submenú manda. Oculto → fuera;
      // Partidas (soloDirectivo) se abre si el perfil le da Lectura o más.
      const nv = nivelRuta(c.to);
      if (nv === 0) return false;
      if (c.soloDirectivo && nv != null) return nv >= 1 && !(c.feature && !features?.[c.feature] && !features?.[`${c.feature}_bloqueado`]);
      // `hideFeature`: bandera NEGATIVA de plataforma (p. ej. zoho_off / metricas_off);
      // si está encendida, el ítem se oculta (al vaciarse la sección su encabezado
      // desaparece solo por la limpieza de encabezados huérfanos de más abajo).
      const grant = c.report && hasReport(c.report); return (!c.cap || can(c.cap) || grant) && (!c.anyCap || c.anyCap.some(can) || grant) && (!c.feature || features?.[c.feature] || features?.[`${c.feature}_bloqueado`]) && !(c.hideFeature && features?.[c.hideFeature]) && (!c.director || isDirector || grant) && (!c.soloDirectivo || esDirectivo) && (!c.soloGerencia || esGerencia) && !(readonly && c.hideReadonly) && !(c.hideCM && user.role === "country_manager"); };
    let kids = n.children.filter((c) => c.heading || visible(c));
    // Quita encabezados huérfanos: un `heading` sin ningún ítem con `to` antes del siguiente encabezado.
    kids = kids.filter((c, i) => {
      if (!c.heading) return true;
      for (let j = i + 1; j < kids.length && !kids[j].heading; j++) if (kids[j].to) return true;
      return false;
    });
    return { ...n, children: kids };
  // Un grupo cuyos hijos (fuera de los encabezados) quedaron todos ocultos no se muestra.
  }).filter((n) => !n.children || n.children.some((c) => c.to));

  // Todo lo que se puede crear, en un solo sitio: lo usan la cabecera y el
  // botón flotante, así ofrecen exactamente lo mismo. Compras solo aparece si
  // el módulo está encendido y la persona tiene acceso.
  const crearAcciones = readonly ? [] : [
    { icon: "checkSquare", label: "Tarea", onClick: createTask },
    ...(can("department_board")
      ? [{ icon: "folder", label: "Proyecto", onClick: crearProyecto }] : []),
    // Ticket de soporte: solo si el módulo está encendido para la organización
    // y la persona puede gestionar tickets.
    ...(features?.soporte && (can("soporte_gestion") || can("soporte_admin"))
      ? [{ icon: "ticket", label: "Ticket", onClick: () => nav("/soporte/tickets", { state: { nuevo: true } }) }] : []),
    // Cliente: directorio unificado Proyectos/Soporte (pedido 2026-08-07: los
    // coordinadores/líderes de equipo de Soporte también deben poder crearlo,
    // no solo quien tiene department_board). El backend (require_clientes) es
    // quien decide de verdad; aquí solo se muestra a quien tiene algo de
    // Proyectos o de Soporte para no ofrecer un atajo que casi nadie usaría.
    ...(can("department_board") || (features?.soporte && can("soporte"))
      ? [{ icon: "briefcase", label: "Cliente", onClick: () => nav("/clientes?nuevo=1") }] : []),
    ...(can("compras") && features?.compras ? [
      { icon: "fileText", label: "Requisición", onClick: () => nav("/compras/requisiciones/nueva") },
      { icon: "receipt", label: "Factura", onClick: () => nav("/compras/facturas/nueva") },
    ] : []),
    // Activo (ITAM): el backend (require_gestion) exige itam_gestion/itam_admin;
    // aquí se refleja igual para no ofrecer un atajo que fallaría al guardar.
    ...(features?.itam && can("itam_gestion")
      ? [{ icon: "laptop", label: "Activo", onClick: () => nav("/itam/activos/nuevo") }] : []),
  ];

  const fabActions = [
    { icon: "message", label: "Mensajería", onClick: () => setDockOpen((v) => !v) },
    { icon: "search", label: "Buscar en la página", onClick: () => document.getElementById("tf-page-search")?.focus() },
    { icon: "grid", label: "Ir al tablero", onClick: () => nav("/departamento") },
    ...crearAcciones.map((a) => ({ ...a, label: `Crear ${a.label.toLowerCase()}` })),
  ];

  return (
    <BadgeCtx.Provider value={badges}>
    <div className="flex min-h-full min-w-0 flex-col overflow-x-hidden">
      {showHelp && <ShortcutsHelp onClose={() => setShowHelp(false)} />}
      <MessagingDock open={dockOpen} onClose={() => setDockOpen(false)} />
      <AnunciosPopup />
      {/* Barra superior: blanca, borde azul tenue, SIN sombra (regla del sistema:
          o borde, o sombra — no ambos). */}
      <header style={{ backgroundColor: "color-mix(in srgb, var(--tf-sidebar-bg, #fff) 95%, transparent)" }} className={`fs-compact fs-menu border-b border-linea backdrop-blur ${navLayout === "erp" ? "fixed inset-x-0 top-0 z-30 h-16" : "sticky top-0 z-30"}`}>
        <div className={`flex items-center gap-2 ${navLayout === "erp" ? "h-16 pl-0 pr-3 sm:pr-4" : "h-14 px-3 sm:px-4"} sm:gap-3`}>
          {navLayout === "erp" && (
            <div className={`hidden h-full shrink-0 items-center border-r border-slate-100 lg:flex ${sidebarCollapsed ? "w-[72px] justify-center gap-1 px-1" : "w-64 justify-between px-5"}`}>
              {features?.mostrar_marca_app !== false && (
                <button className={`flex min-w-0 items-center gap-2 ${sidebarCollapsed ? "justify-center" : "flex-1"}`} onClick={() => nav("/")} title="Ir al inicio">
                  {(sidebarCollapsed ? features?.empresa_favicon || features?.empresa_logo : features?.empresa_logo)
                    ? <img src={`/uploads/${sidebarCollapsed ? features?.empresa_favicon || features?.empresa_logo : features?.empresa_logo}`} alt="" className={`shrink-0 object-contain ${sidebarCollapsed ? "h-6 w-6" : features?.empresa_brand_display !== "logo" ? "h-8 w-8 rounded-lg" : "h-10 w-full max-w-[168px] object-left"}`} />
                    : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-xs font-bold text-white">G+</span>}
                  {!sidebarCollapsed && features?.empresa_brand_display !== "logo" && <span className="truncate text-sm font-bold">{features?.empresa_nombre || <>Gestion<span className="text-brand-600">IT</span></>}</span>}
                </button>
              )}
              <button type="button" onClick={() => {
                const next = !sidebarCollapsed;
                setSidebarCollapsedState(next);
                setSidebarCollapsed(user.id, next);
              }} aria-label={sidebarCollapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
                title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus-visible:ring-2 focus-visible:ring-brand-500">
                <Icon name={sidebarCollapsed ? "chevron-right" : "chevron-left"} size={17} />
              </button>
            </div>
          )}
          {/* Marca propia (logo + "GestionIT"): ocultable en Ajustes → Configuración
              para un despliegue de cliente que no quiere ver la marca de Redes IP. */}
          {features?.mostrar_marca_app !== false && (
            <button className={`flex shrink-0 items-center gap-2 ${navLayout === "erp" ? "lg:hidden" : ""}`} onClick={() => nav("/")}>
              {features?.empresa_logo
                ? <img src={`/uploads/${features.empresa_logo}`} alt="" className={`shrink-0 object-contain ${features?.empresa_brand_display === "logo" ? "h-9 w-28 sm:w-32 xl:w-36" : "h-8 w-8 rounded-lg"}`} />
                : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white">G<span className="text-brand-200">+</span></span>}
              {features?.empresa_brand_display !== "logo" && <span className="hidden text-base font-bold sm:block">{features?.empresa_nombre || <>Gestion<span className="text-brand-600">IT</span></>}</span>}
            </button>
          )}

          {/* min-w-0 + shrink + whitespace-nowrap: con 8 menús, en pantallas
              ~1366px el último ("Herramientas") quedaba cortado por el
              overflow-x-hidden del layout. Ahora el nav puede encoger su
              padding y nunca desaparece un item. */}
          {navLayout !== "erp" && <nav className="ml-2 hidden min-w-0 shrink items-center gap-0.5 lg:flex">
            {items.map((n) => n.children ? (
              <NavDropdown key={n.to} item={n} />
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex flex-col items-center gap-0.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 transition xl:px-3.5 ${isActive ? moduloDe(n.to) : "text-tinta-2 hover:bg-relleno"}`}>
                <Icon name={iconoDe(n.to)} size={15} />
                <span className="text-nav font-semibold">{t(n.k, n.label)}</span>
              </NavLink>
            ))}
          </nav>}

          {navLayout === "erp" && <div className="hidden lg:flex min-w-0 flex-1"><Spotlight open={spotlight} onClose={() => setSpotlight(false)} /></div>}
          {navLayout === "erp" && <div className="lg:hidden"><Spotlight compact open={spotlight} onClose={() => setSpotlight(false)} /></div>}

          <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
            {navLayout !== "erp" && <Spotlight compact open={spotlight} onClose={() => setSpotlight(false)} />}
            {crearAcciones.length > 0 && (
              <div className="relative" ref={crearRef}>
                <button onClick={() => setCrearOpen((o) => !o)}
                  className="btn-outline-primary h-9 whitespace-nowrap" title="Crear">
                  <Icon name="plus" size={16} />
                  <span className="hidden sm:inline"> Crear</span>
                  <Icon name="chevron-down" size={13} className={crearOpen ? "rotate-180" : ""} />
                </button>
                {crearOpen && (
                  <div className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-pop">
                    {crearAcciones.map((a) => (
                      <button key={a.label} onClick={() => { setCrearOpen(false); a.onClick(); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-brand-700">
                        <Icon name={a.icon} size={15} className="text-slate-400" />
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setDockOpen((v) => !v)} className={`relative rounded-lg p-2 hover:bg-slate-100 ${dockOpen ? "text-brand-600" : "text-slate-500"}`} title={t("top.messaging")}>
              <Icon name="message" size={19} />
              {msgUnread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white">{msgUnread > 9 ? "9+" : msgUnread}</span>
              )}
            </button>
            {features?.soporte && (can("soporte_gestion") || can("soporte_admin")) && <WhatsAppBell />}
            <NotificationsBell />
            <HelpButton />
            {navLayout === "erp" ? (
              <div className="flex items-center gap-1 lg:hidden"><SettingsMenu /><AvatarMenu /></div>
            ) : <><SettingsMenu /><AvatarMenu /></>}
            <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setMobileNav((o) => !o)}><Icon name="menu" size={20} /></button>
          </div>
        </div>

        {mobileNav && (
          <nav className="flex flex-col gap-1 border-t border-slate-100 px-3 py-2 lg:hidden">
            {items.map((n) => n.children ? (
              <div key={n.to}>
                <NavLink to={n.to} end onClick={() => setMobileNav(false)}
                  className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isActive ? moduloDe(n.to) : "text-tinta-2"}`}>
                  <Icon name={iconoDe(n.to)} size={15} />
                  {t(n.k, n.label)}
                </NavLink>
                {(n.bloqueado ? [] : n.children.filter(c => c.heading || c.to !== n.to)).map((c) => c.heading ? (
                  <div key={`h-${c.heading}`} className={`px-3 pb-0.5 pt-2 text-[11px] font-bold uppercase tracking-wide ${c.accent === "cyan" ? "text-cyan-600" : "text-brand-600"}`}>{c.heading}</div>
                ) : (
                  <NavLink key={c.to} to={c.to} onClick={() => setMobileNav(false)}
                    className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 pl-7 text-sm ${isActive ? (c.accent === "cyan" ? "text-cyan-700" : "text-brand-700") : "text-slate-500"}`}>
                    {t(c.k, c.label)}
                    {c.badge && <Badge nombre={c.badge} />}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileNav(false)}
                className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isActive ? moduloDe(n.to) : "text-tinta-2"}`}>
                <Icon name={iconoDe(n.to)} size={15} />
                {t(n.k, n.label)}
              </NavLink>
            ))}
            {!readonly && <button onClick={() => { setMobileNav(false); createTask(); }} className="btn-primary mt-1 justify-center"><Icon name="plus" size={16} /> Crear tarea</button>}
          </nav>
        )}
      </header>

      {navLayout === "erp" && <div aria-hidden="true" className="h-16 shrink-0" />}

      {maint?.enabled && (
        <div className="flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-4 py-1.5 text-center text-xs font-semibold text-amber-800">
          <Icon name="shield" size={14} className="shrink-0" />
          <span>Modo mantenimiento activo — solo administradores pueden ingresar.{maint.message ? ` ${maint.message}` : ""}</span>
        </div>
      )}

      {/* `--acento` = color del módulo de la ruta actual. `.btn-primary` lo lee
          (ver index.css), así que el botón primario de cada pantalla toma su
          color de módulo automáticamente — uno de los tres únicos sitios donde
          el documento permite el color de módulo. Ninguna pantalla necesita
          saber su propio color. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {navLayout === "erp" && (
          <>
            <EnterpriseSidebar
              items={items}
              collapsed={sidebarCollapsed}
              location={loc}
              t={t}
            />
            <div aria-hidden="true" className={`hidden shrink-0 lg:block ${sidebarCollapsed ? "w-[72px]" : "w-64"}`} />
          </>
        )}
        <main
          className={`flex min-w-0 flex-1 flex-col${rutaCompacta(loc.pathname) ? " fs-compact" : ""}`}
          style={{ "--acento": acentoDe(loc.pathname) }}
        >
          <div className={`min-w-0 flex-1${features?.soporte && can("soporte") ? " pb-14 sm:pb-0" : ""}`}><Outlet /></div>
          <Footer />
        </main>
      </div>
      {features?.soporte && can("soporte") && <MobileSoporteNav />}
      {/* En móvil el widget sube por encima de la barra inferior (sm:hidden,
          ~64px de alto) para no encimarse con «Nuevo/WhatsApp». En desktop no
          hay barra, vuelve a bottom-6. Pedido 2026-09-19. */}
      <div ref={fabRef} className="fixed bottom-24 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
        {/* Opciones desplegadas por el botón central (aparecen encima del control) */}
        {fabOpen && (
          <div className="mb-1 flex flex-col items-end gap-2">
            {fabActions.map((a) => (
              <button key={a.label} onClick={() => { a.onClick(); setFabOpen(false); }}
                className="flex items-center gap-2 rounded-tag bg-white py-1.5 pl-3 pr-1.5 text-xs font-semibold text-slate-600 shadow-pop ring-1 ring-slate-200/70 transition-all hover:text-brand-700"
                title={a.label}>
                {a.label}
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Icon name={a.icon} size={14} /></span>
              </button>
            ))}
          </div>
        )}
        {/* Control de tres partes: ↑ arriba · + desplegar · ↓ abajo */}
        <div className="flex flex-col overflow-hidden rounded-full text-white opacity-75 shadow-pop ring-1 ring-black/5 transition hover:opacity-100">
          <button data-scroll-intent onClick={scrollTop} title="Ir arriba"
            className="flex h-7 w-8 items-center justify-center bg-brand-600 transition-colors hover:bg-brand-700 active:scale-95">
            <Icon name="arrow-up" size={14} />
          </button>
          <button onClick={() => setFabOpen((o) => !o)} title="Acciones rápidas"
            className={`flex h-8 w-8 items-center justify-center border-y border-white/25 transition-colors active:scale-95 ${fabOpen ? "bg-slate-700 hover:bg-slate-800" : "bg-brand-600 hover:bg-brand-700"}`}>
            <Icon name="plus" size={16} className={`transition-transform ${fabOpen ? "rotate-45" : ""}`} />
          </button>
          <button data-scroll-intent onClick={scrollBottom} title="Ir abajo"
            className="flex h-7 w-8 items-center justify-center bg-brand-600 transition-colors hover:bg-brand-700 active:scale-95">
            <Icon name="arrow-up" size={14} className="rotate-180" />
          </button>
        </div>
      </div>
      <InstallPrompt />
    </div>
    </BadgeCtx.Provider>
  );
}
